/**
 * AR SMS cadence
 *
 * The SMS half of the accounts-receivable ladder that already runs over email
 * in arService.ts. It mirrors the same stages, but keeps its own sent_at
 * columns on `invoices` so an SMS being sent (or not) never affects the email
 * escalation state, and vice versa.
 *
 *   stage        fires when            guard column
 *   due_soon     due tomorrow          ar_sms_due_soon_sent_at
 *   overdue_1    1-2 days overdue      ar_sms_overdue_1_sent_at
 *   overdue_3    3-6 days overdue      ar_sms_overdue_3_sent_at
 *   overdue_7    7+ days overdue       ar_sms_overdue_7_sent_at
 *
 * The guard is the whole point: running the cadence twice in a row must not
 * send twice. A stage is skipped if its column is already stamped, and the
 * column is only stamped after the provider confirms the send — so a failed
 * send is retried on the next run rather than silently swallowed.
 *
 * Nothing here is scheduled. It runs when called, the same way reconcile does
 * for PPSR. Wiring it to a cron is a separate decision.
 */

import { supabase } from '../lib/supabase'
import { sendSMS } from './smsService'

export type ArSmsStage = 'due_soon' | 'overdue_1' | 'overdue_3' | 'overdue_7'

const STAGE_COLUMN: Record<ArSmsStage, string> = {
  due_soon: 'ar_sms_due_soon_sent_at',
  overdue_1: 'ar_sms_overdue_1_sent_at',
  overdue_3: 'ar_sms_overdue_3_sent_at',
  overdue_7: 'ar_sms_overdue_7_sent_at',
}

const PAID_STATUSES = ['paid', 'cancelled', 'written_off']

export interface ArSmsOutcome {
  invoiceId: string
  invoiceNumber: string | null
  stage: ArSmsStage | null
  action:
    | 'sent'
    | 'would_send'
    | 'skipped_already_sent'
    | 'skipped_no_stage'
    | 'skipped_no_mobile'
    | 'skipped_paid'
    | 'skipped_opted_out'
    | 'failed'
  detail?: string
  to?: string | null
}

export interface ArSmsRunSummary {
  checked: number
  sent: number
  failed: number
  skipped: number
  dryRun: boolean
  outcomes: ArSmsOutcome[]
}

export interface ArSmsRunOptions {
  /** Work out what would be sent without sending or stamping anything. */
  dryRun?: boolean
}

/** Whole days from the due date to today. Negative = not due yet. */
export function daysOverdue(dueDate: string | null | undefined, now: Date = new Date()): number | null {
  if (!dueDate) return null
  const due = new Date(dueDate)
  if (isNaN(due.getTime())) return null
  const dueMidnight = Date.UTC(due.getFullYear(), due.getMonth(), due.getDate())
  const nowMidnight = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((nowMidnight - dueMidnight) / 86400000)
}

export function stageForDaysOverdue(days: number | null): ArSmsStage | null {
  if (days === null) return null
  if (days === -1) return 'due_soon'
  if (days >= 1 && days <= 2) return 'overdue_1'
  if (days >= 3 && days <= 6) return 'overdue_3'
  if (days >= 7) return 'overdue_7'
  return null
}

function money(amount: number | null | undefined): string {
  if (amount == null) return ''
  return `$${Math.round(amount).toLocaleString('en-AU')}`
}

function buildMessage(stage: ArSmsStage, invoice: any): string {
  const who = invoice.buyer_name ? `Hi ${invoice.buyer_name}, ` : ''
  const ref = invoice.invoice_number ? ` ${invoice.invoice_number}` : ''
  const amt = money(invoice.total_amount)
  const forAmt = amt ? ` for ${amt}` : ''

  switch (stage) {
    case 'due_soon':
      return `${who}Direct Auto invoice${ref}${forAmt} is due tomorrow. Please contact us if you have any questions.`
    case 'overdue_1':
      return `${who}Direct Auto invoice${ref}${forAmt} is now overdue. Please arrange payment or contact us to discuss.`
    case 'overdue_3':
      return `${who}Direct Auto invoice${ref}${forAmt} is 3 days overdue. Please arrange payment today or contact us.`
    case 'overdue_7':
      return `${who}Direct Auto invoice${ref}${forAmt} is 7 days overdue and a formal demand has issued. Please contact us urgently.`
  }
}

/** The buyer's mobile, preferring the invoice's own value over the deal's. */
function buyerMobile(invoice: any, dealFallback: Record<string, string>): string | null {
  return invoice.buyer_mobile || dealFallback[invoice.deal_id] || null
}

/**
 * Run the cadence over one invoice, or every open invoice when no id is given.
 * Returns what it did to each, so the caller can show or log it.
 */
export async function runArSmsCadence(
  invoiceId?: string,
  opts: ArSmsRunOptions = {}
): Promise<ArSmsRunSummary> {
  const dryRun = opts.dryRun === true
  const summary: ArSmsRunSummary = {
    checked: 0,
    sent: 0,
    failed: 0,
    skipped: 0,
    dryRun,
    outcomes: [],
  }

  let query = supabase
    .from('invoices')
    .select(
      'id, invoice_number, due_date, status, total_amount, buyer_name, buyer_mobile, deal_id, ' +
        'ar_sms_due_soon_sent_at, ar_sms_overdue_1_sent_at, ar_sms_overdue_3_sent_at, ar_sms_overdue_7_sent_at'
    )

  if (invoiceId) query = query.eq('id', invoiceId)

  const { data, error } = await query
  if (error) throw error

  const invoices: any[] = (data as any[]) || []

  // Invoices generated before buyer_mobile was carried across have none of
  // their own. Fall back to the deal's, fetched in one go rather than per row.
  const dealFallback: Record<string, string> = {}
  const needsFallback = invoices
    .filter((i) => !i.buyer_mobile && i.deal_id)
    .map((i) => i.deal_id as string)

  if (needsFallback.length) {
    const { data: deals } = await supabase
      .from('deals')
      .select('id, buyer_mobile')
      .in('id', needsFallback)
    for (const deal of (deals as any[]) || []) {
      if (deal.buyer_mobile) dealFallback[deal.id] = deal.buyer_mobile
    }
  }

  for (const invoice of invoices) {
    summary.checked++
    const record = (o: ArSmsOutcome) => {
      summary.outcomes.push(o)
      if (o.action === 'sent' || o.action === 'would_send') summary.sent++
      else if (o.action === 'failed') summary.failed++
      else summary.skipped++
    }

    if (PAID_STATUSES.includes(String(invoice.status || '').toLowerCase())) {
      record({ invoiceId: invoice.id, invoiceNumber: invoice.invoice_number, stage: null, action: 'skipped_paid' })
      continue
    }

    const stage = stageForDaysOverdue(daysOverdue(invoice.due_date))
    if (!stage) {
      record({ invoiceId: invoice.id, invoiceNumber: invoice.invoice_number, stage: null, action: 'skipped_no_stage' })
      continue
    }

    const guardColumn = STAGE_COLUMN[stage]
    if ((invoice as any)[guardColumn]) {
      record({ invoiceId: invoice.id, invoiceNumber: invoice.invoice_number, stage, action: 'skipped_already_sent' })
      continue
    }

    const mobile = buyerMobile(invoice, dealFallback)
    if (!mobile) {
      record({ invoiceId: invoice.id, invoiceNumber: invoice.invoice_number, stage, action: 'skipped_no_mobile' })
      continue
    }

    if (dryRun) {
      record({
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoice_number,
        stage,
        action: 'would_send',
        to: mobile,
      })
      continue
    }

    const result = await sendSMS({
      to: mobile,
      message: buildMessage(stage, invoice),
      trigger: `AR_SMS_${stage.toUpperCase()}`,
      dealId: invoice.deal_id,
      invoiceId: invoice.id,
    })

    if (!result.success) {
      // A permanent failure — an opted-out recipient, a number the carrier
      // will never accept — fails identically tomorrow. Retrying it daily
      // just fills sms_log with the same row and hides the real failures, so
      // the guard is stamped to close the stage off. The failure is still
      // recorded against the invoice, and the outcome says why.
      if (result.permanent) {
        await supabase
          .from('invoices')
          .update({ [guardColumn]: new Date().toISOString() } as any)
          .eq('id', invoice.id)
      }

      record({
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoice_number,
        stage,
        action: result.optedOut ? 'skipped_opted_out' : 'failed',
        detail: result.permanent ? `${result.message} — not retried` : result.message,
      })
      continue
    }

    // A simulated send is not a send. SMS_PROVIDER defaults to mock, which
    // returns a clean success without contacting a carrier — stamping the
    // guard on that would close the stage off permanently and this buyer would
    // never be chased for this rung again. Leave the guard unset so the next
    // run, on a properly configured environment, actually sends it.
    if (result.simulated) {
      record({
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoice_number,
        stage,
        action: 'failed',
        detail: 'Not sent — this environment has no SMS provider configured. Not counted, will retry.',
      })
      continue
    }

    // Stamp only after the provider confirmed. A failure above leaves the
    // guard unset so the next run retries rather than skipping forever.
    const { error: stampError } = await supabase
      .from('invoices')
      .update({ [guardColumn]: new Date().toISOString() } as any)
      .eq('id', invoice.id)

    if (stampError) {
      record({
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoice_number,
        stage,
        action: 'sent',
        detail: `SENT but guard not stamped: ${stampError.message} — next run may resend`,
      })
      continue
    }

    record({ invoiceId: invoice.id, invoiceNumber: invoice.invoice_number, stage, action: 'sent' })
  }

  return summary
}

export const arSmsCadence = { runArSmsCadence, stageForDaysOverdue, daysOverdue }

export default arSmsCadence
